import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import {
  UserData,
  UsersResponse,
  UsersApiResponse,
  UserDetailResponse,
} from '../models/users.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private apiUrl = `${environment.apiUrl}/Users`;

  constructor(private http: HttpClient) {}

  // Get all users
  getUsers(params?: {
    Role?: string;
    Status?: string;
    Search?: string;
    SortBy?: string;
    Page?: number;
    PageSize?: number;
    IsDeleted?: boolean;
  }): Observable<UsersApiResponse> {
    let queryParams = '';
    if (params) {
      const filteredParams = Object.entries(params)
        .filter(
          ([_, value]) => value !== undefined && value !== null && value !== ''
        )
        .map(([key, value]) => `${key}=${encodeURIComponent(value.toString())}`)
        .join('&');
      
      if (filteredParams) {
        queryParams = `?${filteredParams}`;
      }
    }

    return this.http.get<UsersApiResponse>(
      `${this.apiUrl}/getall${queryParams}`
    );
  }

  // Get users with filters
  getUsersWithFilters(filters: any = {}): Observable<UsersResponse> {
    // Just pass the filters directly to getUsers
    return this.getUsers(filters).pipe(
      map((response) => ({
        data: response.items,
        total: response.totalCount,
        page: response.pageNumber,
        perPage: response.pageSize,
      }))
    );
  }

  // Get user by ID using dedicated endpoint
  getUserById(id: string): Observable<UserDetailResponse> {
    return this.http.get<UserDetailResponse>(
      `${this.apiUrl}/${id}/getuserdetail`
    );
  }

  /**
   * Resolve the signed-in user's GUID by email (search list, then GET .../getuserdetail).
   * Used after login and when local session has no `userId` yet.
   */
  getUserDetailByEmail(email: string): Observable<UserDetailResponse | null> {
    const normalized = email.trim();
    if (!normalized) {
      return of(null);
    }
    const emailLower = normalized.toLowerCase();
    return this.getUsers({
      Search: normalized,
      Page: 1,
      PageSize: 25,
    }).pipe(
      switchMap((response) => {
        const item = response.items.find(
          (u) => (u.emailAddress || '').toLowerCase() === emailLower
        );
        if (!item?.id) {
          return of(null);
        }
        return this.getUserById(item.id);
      }),
      catchError(() => of(null))
    );
  }

  // Get active roles from API
  getActiveRoles(): Observable<{ label: string; value: string }[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/Roles/activeroles`).pipe(
      map((roles) => {
        // Filter out ADMIN role and transform to dropdown format
        const filteredRoles = roles
          .filter((role) => role.name?.toLowerCase() !== 'admin')
          .map((role) => ({
            label: role.name,
            value: role.id,
          }));
        return filteredRoles;
      })
    );
  }

  // Create new user
  createUser(userData: {
    email: string;
    password: string;
    fullName: string;
    roleName: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/createuser`, userData);
  }

  // Edit user
  editUser(
    userId: string,
    userData: {
      fullName: string;
      roleName: string;
      isActive: boolean;
    }
  ): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${userId}/edituser`, userData);
  }

  // Delete user
  deleteUser(userId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${userId}/deleteuser`);
  }

  // Send forgot password email
  sendForgotPasswordEmail(emailAddress: string, message: string = ""): Observable<any> {
    const payload = {
      emailAddress: emailAddress,
      message: message
    };
    return this.http.post<any>(`${this.apiUrl}/sendforgotpasswordemail`, payload);
  }

  // Reset password
  resetPassword(id: string, password: string, confirmPassword: string, message: string = ""): Observable<any> {
    const payload = {
      id: id,
      password: password,
      confirmPassword: confirmPassword,
      message: message
    };
    return this.http.post<any>(`${this.apiUrl}/resetpassword`, payload);
  }

  // Reset account password (for logged-in users)
  resetAccountPassword(id: string, oldPassword: string, password: string, message: string = ""): Observable<any> {
    const payload = {
      id: id,
      oldPassword: oldPassword,
      password: password,
      message: message
    };
    return this.http.post<any>(`${this.apiUrl}/resetaccountpassword`, payload);
  }
}
